import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRecoilValue } from 'recoil';
import classNames from 'classnames';
import { Button } from '@ui-elements';
import { UserLocationState } from '~/common/store';
import { EnquiryField } from './components/enquiry-field';
import {
  buildDefaultValues,
  buildFormSchema,
  isFieldVisible,
  sortFields,
} from './form-schema';
import {
  EnquirySubmission,
  EnquirySubmitted,
  buildSubmission,
} from './lead-payload';
import {
  RecaptchaCheckbox,
  RecaptchaHandle,
} from './components/recaptcha-checkbox';
import { EnquiryVehicle, handOffEnquirySubmitted } from './analytics';
import { EnquiryForm, EnquiryFormField, EnquiryFormValues } from './types';
import styles from './index.module.scss';

export type { EnquirySubmission, EnquirySubmitted } from './lead-payload';
export type { EnquiryForm } from './types';

// Fired by the thank you page, which is where the confirmation lives now. Exported from here so the page
// imports one module rather than reaching into this folder's internals.
export { pushHandedOffEnquiry } from './analytics';

interface OfferEnquiryFormProps {
  /** The definition, from /api/v2/offers/{uniqueName}/enquiry-form. */
  form: EnquiryForm;
  offerUniqueName: string;
  /**
   * reCAPTCHA site key, passed down from the page. Without one the check cannot be shown, and the form says
   * so rather than offering a submit that the server will always refuse.
   */
  recaptchaSiteKey?: string;
  /**
   * The vehicle this enquiry is about, for the analytics event. The page knows it; this component does not,
   * and should not have to derive it from an offer it never sees.
   */
  vehicle?: EnquiryVehicle;
  /**
   * Sends the enquiry. The renderer deliberately does not know how - the transport belongs to the caller,
   * and on this site publishing a lead is a server side concern with reCAPTCHA attached. Reject to have the
   * form show its error and stay filled in.
   */
  onSubmit: (submission: EnquirySubmission) => Promise<EnquirySubmitted | void>;
  /**
   * Called once the enquiry is away and the analytics event has been handed over. The page takes the
   * customer to the thank you page from here.
   *
   * Required, and deliberately not optional: this form has no confirmation of its own any more, so a caller
   * that did nothing here would leave a customer looking at a form they have already submitted.
   */
  onSubmitted: () => void;
  className?: string;
}

/**
 * Renders an enquiry form from a definition authored in the backoffice.
 *
 * Nothing about the fields is hardcoded: the definition decides which inputs exist, their order, their
 * widths, what is required, and where each answer lands in Salesforce. Validation is generated from the same
 * definition, so the two cannot disagree.
 */
export const OfferEnquiryForm: React.FC<OfferEnquiryFormProps> = ({
  form,
  offerUniqueName,
  recaptchaSiteKey,
  vehicle,
  onSubmit,
  onSubmitted,
  className,
}) => {
  const fields = useMemo(() => sortFields(form.fields), [form.fields]);
  const schema = useMemo(() => buildFormSchema(fields), [fields]);
  const defaultValues = useMemo(() => buildDefaultValues(fields), [fields]);

  const [isSent, setIsSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [recaptchaUnavailable, setRecaptchaUnavailable] = useState(false);
  const recaptcha = React.useRef<RecaptchaHandle>(null);

  const { control, handleSubmit, formState, setValue, watch } =
    useForm<EnquiryFormValues>({
      resolver: zodResolver(schema),
      // onChange, not onBlur: the design greys the button out until the form is valid, and isValid only
      // keeps up on change. Same mode the site's subscribe form uses for the same reason.
      mode: 'onChange',
      defaultValues,
    });

  // watch() rather than useWatch: it is the form API already used elsewhere in this codebase, and this
  // repository's TypeScript cannot parse the installed react-hook-form's useWatch typings.
  const values = watch();
  const location = useRecoilValue(UserLocationState);
  const postcodeField = fields.find(
    (field) => field.contentType === 'postcodeField'
  );

  // The postcode comes from the suburb already chosen for the site, so it is pushed into the form value
  // rather than asked for twice. Changing suburb in the site's modal updates the form with it.
  useEffect(() => {
    if (!postcodeField) return;
    setValue(postcodeField.fieldName, location?.postcode ?? '', {
      shouldValidate: Boolean(location?.postcode),
    });
  }, [postcodeField, location?.postcode, setValue]);

  const visibleFields = fields.filter((field) => isFieldVisible(field, values));

  // No key, or a widget that never loaded, both mean the same thing: the server will refuse whatever this
  // form sends, so it must not offer to send it. Said once, here, rather than leaving the button live and
  // contradicting the message beside the check.
  const verificationUnavailable = !recaptchaSiteKey || recaptchaUnavailable;

  const submit = async (submitted: EnquiryFormValues) => {
    setSubmitError(null);

    if (verificationUnavailable) {
      setSubmitError(
        'The verification check could not be loaded, so this enquiry cannot be sent. Please refresh the page and try again.'
      );
      return;
    }

    if (!recaptchaToken) {
      setSubmitError('Please confirm you are not a robot.');
      return;
    }

    try {
      const published = await onSubmit(
        buildSubmission(form, offerUniqueName, submitted, recaptchaToken ?? '')
      );
      // Latched before leaving: the redirect is not instant, and a form that still looks submittable invites
      // a second enquiry from the same customer.
      setIsSent(true);

      // Awaited, unlike the fire-and-forget this replaced, because the event has to be written down before
      // the page is torn down - it is pushed on the thank you page. Nothing here can fail the submission:
      // handOffEnquirySubmitted swallows its own errors, so this only waits for the hashing.
      await handOffEnquirySubmitted(
        form,
        submitted,
        offerUniqueName,
        vehicle,
        published?.externalLeadId
      );

      onSubmitted();
    } catch (error) {
      // Left filled in on purpose: a failed send must not cost the customer their answers. The token is
      // spent either way - reCAPTCHA accepts one verification per token - so the widget is reset and the
      // customer ticks it again with everything they typed still there.
      recaptcha.current?.reset();
      setSubmitError(
        'Your enquiry could not be sent. Please check your details and try again.'
      );
    }
  };

  return (
    <section
      className={classNames(styles.enquiryForm, className)}
      data-testid="enquiry-form"
      data-form-id={form.formId}
    >
      {/* Title and blurb are rich text from the backoffice, including their alignment. */}
      {!!form.title && (
        <div
          className={styles.title}
          dangerouslySetInnerHTML={{ __html: form.title }}
        />
      )}
      {!!form.blurb && (
        <div
          className={styles.blurb}
          dangerouslySetInnerHTML={{ __html: form.blurb }}
        />
      )}

      <form onSubmit={handleSubmit(submit)} noValidate>
        <div className={styles.fields}>
          {visibleFields.map((field) => (
            <div
              key={field.fieldName}
              className={classNames(widthClass(field), {
                [styles.required]: field.fieldIsRequired,
              })}
            >
              <EnquiryField
                field={field}
                control={control}
                error={
                  formState.errors[field.fieldName]?.message as
                    | string
                    | undefined
                }
              />
            </div>
          ))}
        </div>

        {!recaptchaSiteKey && (
          <p className={styles.submitError} role="alert">
            This form cannot be sent at the moment. Please try again later.
          </p>
        )}

        {!!recaptchaSiteKey && (
          <RecaptchaCheckbox
            ref={recaptcha}
            siteKey={recaptchaSiteKey}
            onChange={setRecaptchaToken}
            onUnavailable={setRecaptchaUnavailable}
            className={styles.recaptcha}
          />
        )}

        {!!submitError && (
          <p
            className={styles.submitError}
            role="alert"
            data-testid="enquiry-form-error"
          >
            {submitError}
          </p>
        )}

        <div className={styles.actions}>
          <Button
            nativeType="submit"
            buttonSize="large"
            className={styles.submit}
            // Still spinning once the enquiry is away: the customer is waiting for the thank you page, and a
            // button that went back to normal in the meantime reads as a submit that did nothing.
            loading={formState.isSubmitting || isSent}
            disabled={
              !formState.isValid ||
              formState.isSubmitting ||
              isSent ||
              verificationUnavailable
            }
            data-testid="enquiry-form-submit"
          >
            {form.submitButtonText || 'Submit'}
          </Button>
        </div>
      </form>
    </section>
  );
};

function widthClass(field: EnquiryFormField): string {
  if (field.fieldWidth === 'Full') return styles.full;
  if (field.fieldWidth === 'Third') return styles.third;
  return styles.half;
}
